import { Grid } from "@mui/material";
import { useCallback } from "react";
import { alertService } from "Apis";
import { useSelectedProject } from "Hooks";
import { ApiAlertResponse } from "@incendium/api";
import { useHistory } from "react-router-dom";
import { useAlerts } from "Hooks/useAlerts";
import produce from "immer";
import { useConfirmation } from "Hooks/useConfirmation";
import withPagetitle, { IWithPagetitleProps } from "HoC/withPagetitle";
import AlertsList from "features/alerts/components/AlertsList";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { RouteNames } from "Hooks/routeNames";

function AlertsPageInner() {
  const history = useHistory();
  const { selectedProject: project } = useSelectedProject();
  const { alerts, setAlerts } = useAlerts();
  const handleDelete = useConfirmation();
  const { pathByName } = useProtectedRoutes();

  const onEdit = useCallback(
    (alert: ApiAlertResponse) => {
      history.push(
        pathByName(RouteNames.ALERT, {
          clientId: project?.clientId,
          projectId: project?.id,
          alertId: alert.id,
        }),
      );
    },
    [history, pathByName, project],
  );

  const goToNew = useCallback(() => {
    history.push(
      pathByName(RouteNames.ALERT, {
        clientId: project?.clientId,
        projectId: project?.id,
        alertId: "new",
      }),
    );
  }, [history, pathByName, project]);

  const onDelete = useCallback(
    (alert: ApiAlertResponse) => {
      handleDelete({
        title: `Are you sure you want to delete this alert`,
        body: "This action can not be undone",
        callback: async () => {
          await alertService.alertServiceDeleteAlert({
            projectId: project?.id as number,
            alertId: alert.id as number,
          });

          setAlerts(
            produce(alerts, (draft) => {
              const idx = alerts.findIndex((a) => alert.id === a.id);
              if (idx !== -1) draft.splice(idx, 1);
            }),
          );
          return `Alert (${alert.name}) Deleted`;
        },
      });
    },
    [alerts, handleDelete, project?.id, setAlerts],
  );

  return (
    <Grid
      container
      spacing={3}
      alignItems="center"
      justifyContent="space-between"
    >
      <Grid item xs={12}>
        <AlertsList
          alerts={alerts}
          onNew={goToNew}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </Grid>
    </Grid>
  );
}

const EnhancedComponent = withPagetitle(
  AlertsPageInner,
) as React.ComponentType<IWithPagetitleProps>;

function AlertsPage() {
  const { selectedProject } = useSelectedProject();
  return (
    <EnhancedComponent
      title={`Manage ${selectedProject?.name} Alerts`}
      subTitle="Create Alerts to inform your self of unusual behaviour on your site."
    />
  );
}

export default AlertsPage;
